import { Link } from "react-router-dom";
import { Card, CardContent } from "@learn-me/ui";
import { FadeIn } from "@/components/animated";
import { OfferingRequestForm } from "@/features/offerings/components/OfferingRequestForm";
import { Layers, Route, Check, ArrowRight } from "lucide-react";

const SOLUTIONS = [
  {
    id: "plataforma",
    icon: Layers,
    kicker: "Tecnología a la medida de su organización",
    title: "Una plataforma diseñada para funcionar como su empresa necesita",
    paragraphs: [
      "En Arcus desarrollamos plataformas empresariales personalizadas, creadas alrededor de las necesidades de cada organización. Cada solución puede contar con su propio dominio, identidad visual, estructura, perfiles y funcionalidades.",
      "En un mismo entorno se pueden integrar capacitación, rutas formativas, inducción, preselección, evaluaciones, cursos complementarios, recursos internos, desarrollo de colaboradores, seguimiento y trazabilidad, organizados por áreas, puestos o poblaciones.",
      "La plataforma puede comenzar con lo que su empresa necesita hoy e incorporar nuevas funcionalidades conforme sus necesidades evolucionan.",
    ],
    closing: "Una plataforma que se adapta a su empresa y crece con ella.",
    highlights: [
      "Dominio e identidad visual propios",
      "Perfiles y estructura por áreas o puestos",
      "Seguimiento y trazabilidad del proceso",
    ],
    cta: "Solicite su propuesta",
  },
  {
    id: "rutas",
    icon: Route,
    kicker: "Rutas formativas a la medida",
    title: "De una necesidad de desarrollo a una ruta completa",
    paragraphs: [
      "Transformamos una necesidad de capacitación en un proceso de desarrollo. En Arcus Learning diseñamos rutas formativas adaptadas a los objetivos, las personas y la realidad de cada organización. En lugar de limitar el aprendizaje a una capacitación aislada, construimos un recorrido que permita desarrollar conocimientos y competencias de forma progresiva.",
      "Cada ruta puede combinar capacitaciones presenciales o virtuales, cursos digitales, contenidos especializados, actividades prácticas, evaluaciones y recursos complementarios.",
      "Además, puede integrarse a la plataforma personalizada de la organización para asignar diferentes recorridos según áreas, puestos o poblaciones y mantener seguimiento y trazabilidad del proceso formativo.",
    ],
    closing: "Del aprendizaje puntual al desarrollo continuo.",
    highlights: [
      "Recorridos progresivos por área o puesto",
      "Combina lo presencial, lo virtual y lo autogestionado",
      "Integrable a la plataforma de la organización",
    ],
    cta: "Quiero diseñar una ruta formativa",
  },
];

export default function EnterprisePage() {
  return (
    <div className="bg-white">
      <section className="bg-gradient-to-b from-secondary-50 to-white border-b">
        <div className="container py-16 md:py-20">
          <FadeIn>
            <div className="max-w-3xl">
              <h1 className="font-display text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
                Soluciones que se adaptan a su organización
              </h1>
              <p className="text-lg text-secondary-600 leading-relaxed">
                No todas las organizaciones funcionan igual ni necesitan las
                mismas herramientas. En Arcus Learning desarrollamos soluciones
                que parten de la realidad de cada empresa o institución y se
                construyen alrededor de sus procesos, personas y objetivos.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <div className="container py-16 space-y-20">
        {SOLUTIONS.map((solution, index) => {
          const Icon = solution.icon;
          return (
            <FadeIn key={solution.id}>
              <section
                id={solution.id}
                className="grid lg:grid-cols-2 gap-10 items-start"
              >
                <div className={index % 2 === 1 ? "lg:order-2" : undefined}>
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary-100 text-primary-700 mb-5">
                    <Icon className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-primary-700 mb-3">
                    {solution.kicker}
                  </p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-secondary-900 mb-5">
                    {solution.title}
                  </h2>
                  {solution.paragraphs.map((paragraph) => (
                    <p
                      key={paragraph.slice(0, 40)}
                      className="text-secondary-600 leading-relaxed mb-4"
                    >
                      {paragraph}
                    </p>
                  ))}
                  <p className="text-secondary-900 font-semibold mb-6">
                    {solution.closing}
                  </p>
                  <ul className="space-y-2">
                    {solution.highlights.map((highlight) => (
                      <li
                        key={highlight}
                        className="flex items-start gap-2 text-secondary-700"
                      >
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <Card className={index % 2 === 1 ? "lg:order-1" : undefined}>
                  <CardContent className="p-6">
                    <h3 className="font-display text-xl font-bold text-secondary-900 mb-2">
                      {solution.cta}
                    </h3>
                    <p className="text-sm text-secondary-600 mb-6">
                      Compártanos brevemente qué necesidad desea resolver y
                      nuestro equipo se pondrá en contacto.
                    </p>
                    <OfferingRequestForm
                      contextLabel={solution.kicker}
                      submitLabel={solution.cta}
                    />
                  </CardContent>
                </Card>
              </section>
            </FadeIn>
          );
        })}
      </div>

      <section className="border-t bg-secondary-50">
        <div className="container py-14 text-center">
          <h2 className="font-display text-2xl font-bold text-secondary-900 mb-3">
            ¿Busca formación para su equipo?
          </h2>
          <p className="text-secondary-600 mb-6">
            Conozca nuestros programas y capacitaciones para empresas e
            instituciones.
          </p>
          <Link
            to="/programas"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-secondary-900 hover:bg-secondary-800 text-white font-semibold transition-colors"
          >
            Ver programas y capacitaciones
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
